import {Injectable} from '@angular/core';
import {BehaviorSubject,Observable} from "rxjs";
import {LoginType,PasswordParams} from "@domain/admin/parametre/authentification/generalites/password-params";
import {Result} from "@domain/common/http/result";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {handleResult} from "@share/utils/handle-result";
import {finalize} from "rxjs/operators";
import {GeneralitesHttpService} from "@services/admin/parametre/authentification/generalites-http.service";

/**
 * Service de gestion de PasswordParams
 *
 * @author Lionel Gimenez
 * @date 25/07/2025
 */
@Injectable()
export class PasswordParamsService {
    /** Indicateur pour savoir si on est en cours de sauvegarde */
    isSaving: boolean = false;

    /** Subject sur PasswordParams */
    private passwordParamsSubject: BehaviorSubject<PasswordParams> = new BehaviorSubject<PasswordParams>(null);

    /**
     * Constructeur
     */
    constructor(
        private generalitesHttpService: GeneralitesHttpService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
    ) {
        //On charge les données
        this.load();
    }

    /**
     * Charge les données
     */
    load(): void {
        this.generalitesHttpService.loadListePasswordParams()
            .pipe(handleResult(this.translateService,this.toastrService,null,'global.errors.generic'))
            .subscribe((result: Result) => {
                if (!result) return;
                const listePasswordParams = result.data.listePasswordParams;
                let passwordParams: PasswordParams;

                //Si on a bien reçu une valeur, on a normalement qu'une valeur sur le tableau
                if (listePasswordParams != null && listePasswordParams[0] != 0) {
                    //On attribue la première valeur en donnée courante
                    passwordParams = listePasswordParams[0];
                } else {
                    //On n'a pas de données on initialise le modèle
                    passwordParams = this.initPasswordParams();
                }

                this.passwordParamsSubject.next(passwordParams);
            });
    }

    /**
     * Sauvegarde les données
     */
    save(): void {
        //On récupère le PasswordParam courant
        const passwordParams = this.passwordParamsSubject.getValue();

        //On change l'indicateur de sauvegarde
        this.isSaving = true;

        //On le sauvegarde
        this.generalitesHttpService.savePasswordParams(passwordParams)
            .pipe(
                handleResult(this.translateService,this.toastrService),
                finalize(() => this.isSaving = false)
            )
            .subscribe();
    }

    /**
     * Retourne un observable de PasswordParams
     */
    getCurrent(): Observable<PasswordParams> {
        return this.passwordParamsSubject.asObservable();
    }

    /**
     * Initialise PasswordParams
     */
    private initPasswordParams(): PasswordParams {
        //Création
        const passwordParams: PasswordParams = new PasswordParams();
        passwordParams.loginType = LoginType.LOCAL;
        passwordParams.minSpecial = 0;
        passwordParams.minMinuscule = 1;
        passwordParams.minMajuscule = 1;
        passwordParams.minChiffre = 1;
        passwordParams.historique = 3;
        passwordParams.peremptionNbJour = 30;
        passwordParams.tentativeNb = 3;
        passwordParams.minLength = 12;
        passwordParams.recaptchaActif = true;

        return passwordParams;
    }
}